/**
 * 個人設定管理(現在は、テーブルの並び替えを主に扱っている)
 */
import { objectFromTupleAndValue } from '@/utils/createObject';
import { getOrganizationId } from '@/utils/token';
import { isTableType, tableType } from '@/coreDomainsModule/table';
import { getPreference, putPreference } from '../preferenceManage';
import { TableProperty } from '@/coreDomainsModule/table';
import { supportedLang } from '@/coreDomainsModule/i18n';
import { z } from 'zod';
import isEqual from 'lodash/isEqual';
// 本当はクラス化して、setter/getterをちゃんと定義すべきだが、
// それほど重要なデータでもなさそうなので、実装を楽にするためにオブジェクトを直接exportする

/**
 * テーブルの並び替えの設定
 * tableHeaderList: テーブルのヘッダーの情報（表示する順番含め）を管理する
 * hideHeaderList: テーブルのヘッダーの情報（非表示の項目）を管理する(tableHeaderList ⊇ hideHeaderList)
 * variableTableHeader: テーブルが持っているユーザー定義ヘッダーの情報を管理する
 * columnWidths: テーブルの列幅を管理する
 * filterInfos: テーブルのフィルター情報を管理する
 */
const TableContentSchema = z.object({
  tableHeaderList: z.array(z.string()).default([]),
  hideHeaderList: z.array(z.string()).default([]),
  // NOTE: variableTableHeader の定義は coreDomainsModule/table.ts/TableProperty の情報をコピーしています
  // どちらかの定義を変更する際はもう一方も変えてください
  variableTableHeader: z
    .array(
      z.object({
        customFormulaTitle: z.string().optional(),
        customFormula: z.array(z.string()).optional(),
        customFormulaId: z.number().optional(),
      })
    )
    .default([]),
  columnWidths: z.record(z.string(), z.number()).default({}),
  filterInfos: z
    .record(
      z.string(),
      z.array(z.union([z.string(), z.number()])).transform((items): string[] => items.map((item) => String(item)))
    )
    .default({}),
  orderStatusFilter: z.record(z.string(), z.boolean()).default({}),
  sortKey: z.string().optional(),
  isAsc: z.boolean().optional(),
  pageSize: z.number().optional(),
});
type TableContent = z.infer<typeof TableContentSchema>;
let myPreferences = objectFromTupleAndValue<typeof tableType, TableContent>(tableType, {
  tableHeaderList: [],
  hideHeaderList: [],
  variableTableHeader: [],
  columnWidths: {},
  filterInfos: {},
  orderStatusFilter: {},
  sortKey: undefined,
  isAsc: undefined,
  pageSize: undefined,
});

/**
 * ログイン直後に個人情報を読み込む。サーバになければデフォルトを使う
 */
const loadPreferences = async () => {
  // サーバから取得
  const tableContentInfo = await getPreference(await getOrganizationId(), 'tableContentInfo');
  if (tableContentInfo != null) {
    // 取得したデータをスキーマに合うように加工する
    const invalidKeys: string[] = [];
    const validTableContentInfoEntries: [string, unknown][] = [];
    for (const [key, value] of Object.entries(tableContentInfo)) {
      if (!isTableType(key)) {
        invalidKeys.push(key);
        continue;
      }
      validTableContentInfoEntries.push([key, TableContentSchema.strip().parse(value)]);
    }
    if (invalidKeys.length > 0) {
      console.warn(`IndexedDB内に不正なtableKeyの値が入っています: ${invalidKeys}`);
    }
    const normalizedTableContentInfo: Record<keyof typeof tableType, TableContent> = Object.fromEntries(
      validTableContentInfoEntries
    ) as Record<keyof typeof tableType, TableContent>;
    // myPropertyPreferencesに保存
    myPreferences = normalizedTableContentInfo;
    // もしDB上のスキーマと現在のスキーマが違う場合は、現在のスキーマのオブジェクトを保存する
    if (!isEqual(tableContentInfo, normalizedTableContentInfo)) {
      console.log('Updated tableContentInfo schemas');
      await savePreferences();
    }
  }
  // サーバにもないなら初期設定のまま
};
/**
 *
 */
const savePreferences = async () => {
  await putPreference(await getOrganizationId(), 'tableContentInfo', myPreferences);
};

/**
 * フィルター情報を全て削除する
 */
const removeAllFilterInfos = async () => {
  const updatedPreference = Object.fromEntries(
    Object.entries(myPreferences).map(([key, value]) => [key, { ...value, filterInfos: {} }])
  ) as typeof myPreferences;
  myPreferences = updatedPreference;
  savePreferences();
};

const combineOrderDefaultCondSchemaObject = z
  .object({
    totalOrderAmount: z.number().default(0),
    turnOverUpperLimit: z.number().default(0),
    isMOQConsidered: z.boolean().default(true),
  })
  .default({
    totalOrderAmount: 0,
    turnOverUpperLimit: 0,
    isMOQConsidered: true,
  });

/**
 * 出力日付フォーマット
 */
const outputDateFormatValues = ['YYYYMMDD', 'YYYY/MM/DD', 'YYYY-MM-DD'] as const;
type OutputDateFormat = (typeof outputDateFormatValues)[number];

/**
 * 個人設定
 * simulation: シミュレーション関係の設定
 * language: 言語設定
 * order: 発注関係の設定
 * outputDateFormat: 出力日付フォーマット設定
 */
const MyPropertySchemaObject = z
  .object({
    simulation: z
      .object({
        graphRange: z.number().optional(),
      })
      .default({
        graphRange: undefined,
      }),
    language: z.enum(supportedLang).default('ja'),
    outputDateFormat: z.enum(outputDateFormatValues).default('YYYY/MM/DD'),
    order: z
      .object({
        unpaidOrderedOrderAppearance: z.boolean().default(true),
        orderReminderAppearance: z.boolean().default(true),
        combineOrderDefaultCond: z
          .object({
            primary: combineOrderDefaultCondSchemaObject,
            secondary: combineOrderDefaultCondSchemaObject,
          })
          .default({
            primary: {
              totalOrderAmount: 0,
              turnOverUpperLimit: 0,
              isMOQConsidered: true,
            },
            secondary: {
              totalOrderAmount: 0,
              turnOverUpperLimit: 0,
              isMOQConsidered: true,
            },
          }),
      })
      .default({
        unpaidOrderedOrderAppearance: true,
        orderReminderAppearance: true,
        combineOrderDefaultCond: {
          primary: {
            totalOrderAmount: 0,
            turnOverUpperLimit: 0,
            isMOQConsidered: true,
          },
          secondary: {
            totalOrderAmount: 0,
            turnOverUpperLimit: 0,
            isMOQConsidered: true,
          },
        },
      }),
  })
  .strip();

const MyPropertySchema = MyPropertySchemaObject.default({
  simulation: {
    graphRange: undefined,
  },
  language: 'ja',
  outputDateFormat: 'YYYY/MM/DD',
  order: {
    unpaidOrderedOrderAppearance: true,
    orderReminderAppearance: true,
    combineOrderDefaultCond: {
      primary: {
        totalOrderAmount: 0,
        turnOverUpperLimit: 0,
        isMOQConsidered: true,
      },
      secondary: {
        totalOrderAmount: 0,
        turnOverUpperLimit: 0,
        isMOQConsidered: true,
      },
    },
  },
});

type MyPropertyObject = z.infer<typeof MyPropertySchema>;

let myPropertyPreferences: MyPropertyObject = {
  simulation: {
    graphRange: undefined,
  },
  language: 'ja',
  outputDateFormat: 'YYYY/MM/DD',
  order: {
    unpaidOrderedOrderAppearance: true,
    orderReminderAppearance: true,
    combineOrderDefaultCond: {
      primary: {
        totalOrderAmount: 0,
        turnOverUpperLimit: 0,
        isMOQConsidered: true,
      },
      secondary: {
        totalOrderAmount: 0,
        turnOverUpperLimit: 0,
        isMOQConsidered: true,
      },
    },
  },
};

/**
 * ログイン直後に個人情報を読み込む。サーバになければデフォルトを使う
 */
const loadMyPropertyPreferences = async () => {
  // サーバから取得
  const myPropertyPreferencesInfo = await getPreference(await getOrganizationId(), 'myPropertyInfo');
  if (myPropertyPreferencesInfo != null) {
    // 取得したデータをスキーマに合うように加工する
    const normalizedMyPropertyPreferencesInfo = MyPropertySchema.parse(myPropertyPreferencesInfo);
    // myPropertyPreferencesに保存
    myPropertyPreferences = normalizedMyPropertyPreferencesInfo;
    // もしDB上のスキーマと現在のスキーマが違う場合は、現在のスキーマのオブジェクトを保存する
    if (!isEqual(myPropertyPreferencesInfo, normalizedMyPropertyPreferencesInfo)) {
      console.log('Updated myPropertyInfo schemas');
      await saveMyPropertyPreferences();
    }
  }
  // サーバにもないなら初期設定のまま
};
/**
 *
 */
const saveMyPropertyPreferences = async () => {
  return await putPreference(await getOrganizationId(), 'myPropertyInfo', myPropertyPreferences);
};

export {
  myPreferences,
  loadPreferences,
  savePreferences,
  removeAllFilterInfos,
  myPropertyPreferences,
  loadMyPropertyPreferences,
  saveMyPropertyPreferences,
  outputDateFormatValues,
};
export type { OutputDateFormat };
